import { Vector3 } from 'three';
import { TrackDef } from './spline';
import { Centerline } from './centerline';

export interface CombQuill {
  s: number;
  pos: Vector3;
  dir: Vector3; // direction the quill points (away from the curve centre)
  curvature: number;
}

/** Data for the curvature comb overlay — you cannot fix what you cannot see. */
export function curvatureComb(centerline: Centerline, spacing = 1): CombQuill[] {
  const L = centerline.totalLength;
  const n = Math.max(2, Math.ceil(L / spacing));
  const quills: CombQuill[] = [];
  for (let i = 0; i <= n; i++) {
    const s = (L * i) / n;
    const pos = centerline.posAt(s);
    const kvec = centerline.curvatureVecAt(s);
    const k = kvec.length();
    const dir = k > 1e-9 ? kvec.clone().divideScalar(k).negate() : new Vector3(0, 1, 0);
    quills.push({ s, pos, dir, curvature: k });
  }
  return quills;
}

/**
 * Smooth-handles: set the handles of nodes [i0..i1] so the chain satisfies the
 * C² cubic-spline condition through the node positions, using chord-length
 * knots (node spacing is far from uniform on a real layout). Tridiagonal solve
 * per axis for the node derivatives D_i, then handles = ±D_i·h/3.
 *
 * Full-track calls (default range) use natural end conditions on open tracks.
 * Partial ranges clamp end tangents to the current handles so they stay C¹
 * with untouched neighbours.
 */
export function smoothHandles(track: TrackDef, i0?: number, i1?: number): void {
  const n = track.nodes.length;
  if (n < 2) return;
  const lo = i0 ?? 0;
  const hi = i1 ?? n - 1;
  const full = lo === 0 && hi === n - 1;

  if (track.closed && full) {
    smoothClosedAll(track);
    return;
  }

  const count = hi - lo + 1;
  if (count < 2) return;
  const P = track.nodes.slice(lo, hi + 1).map((nd) => nd.p);
  const h: number[] = [];
  for (let i = 0; i < count - 1; i++) h.push(Math.max(P[i].distanceTo(P[i + 1]), 1e-6));

  const D =
    full && !track.closed
      ? solveNaturalSpline(P, h)
      : solveClampedSpline(
          P,
          h,
          track.nodes[lo].hOut.clone().multiplyScalar(3 / h[0]),
          track.nodes[hi].hIn.clone().multiplyScalar(-3 / h[count - 2]),
        );

  for (let i = 0; i < count; i++) {
    const node = track.nodes[lo + i];
    if (i < count - 1) node.hOut.copy(D[i]).multiplyScalar(h[i] / 3);
    if (i > 0) node.hIn.copy(D[i]).multiplyScalar(-h[i - 1] / 3);
    node.mode = 'smooth';
  }
}

/** Recompute every handle so the track is a C² spline through the control points. */
export function autosmoothTrack(track: TrackDef): void {
  smoothHandles(track);
}

/**
 * Natural (free-end) non-uniform cubic spline. Zero second derivative at the
 * ends: the curve eases through the terminal points without an imposed tangent.
 */
function solveNaturalSpline(P: Vector3[], h: number[]): Vector3[] {
  const n = P.length;
  const D: Vector3[] = Array.from({ length: n }, () => new Vector3());
  if (n === 2) {
    const d = P[1].clone().sub(P[0]).divideScalar(h[0]);
    D[0].copy(d);
    D[1].copy(d);
    return D;
  }

  const a = new Float64Array(n);
  const b = new Float64Array(n);
  const c = new Float64Array(n);
  // natural start: 2 D0 + D1 = 3 (P1−P0)/h0
  b[0] = 2 / h[0];
  c[0] = 1 / h[0];
  for (let i = 1; i < n - 1; i++) {
    const hPrev = h[i - 1];
    const hNext = h[i];
    a[i] = 1 / hPrev;
    b[i] = 2 * (1 / hPrev + 1 / hNext);
    c[i] = 1 / hNext;
  }
  // natural end: D_{n-2} + 2 D_{n-1} = 3 (P_{n-1}−P_{n-2})/h
  a[n - 1] = 1 / h[n - 2];
  b[n - 1] = 2 / h[n - 2];

  for (const axis of ['x', 'y', 'z'] as const) {
    const rhs = new Float64Array(n);
    rhs[0] = (3 * (P[1][axis] - P[0][axis])) / (h[0] * h[0]);
    for (let i = 1; i < n - 1; i++) {
      const hPrev = h[i - 1];
      const hNext = h[i];
      rhs[i] =
        3 *
        ((P[i][axis] - P[i - 1][axis]) / (hPrev * hPrev) +
          (P[i + 1][axis] - P[i][axis]) / (hNext * hNext));
    }
    rhs[n - 1] = (3 * (P[n - 1][axis] - P[n - 2][axis])) / (h[n - 2] * h[n - 2]);
    const x = thomas(a, b, c, rhs);
    for (let i = 0; i < n; i++) D[i][axis] = x[i];
  }
  return D;
}

/**
 * Clamped non-uniform cubic spline. C² condition at interior node i:
 * D_{i−1}/h_{i−1} + 2·D_i·(1/h_{i−1}+1/h_i) + D_{i+1}/h_i
 *   = 3·[ (P_i−P_{i−1})/h_{i−1}² + (P_{i+1}−P_i)/h_i² ]
 */
function solveClampedSpline(P: Vector3[], h: number[], d0: Vector3, dn: Vector3): Vector3[] {
  const n = P.length;
  const D: Vector3[] = new Array(n);
  D[0] = d0.clone();
  D[n - 1] = dn.clone();
  if (n <= 2) return D;

  const m = n - 2;
  const a = new Float64Array(m);
  const b = new Float64Array(m);
  const c = new Float64Array(m);
  for (let i = 0; i < m; i++) {
    const hPrev = h[i];
    const hNext = h[i + 1];
    a[i] = 1 / hPrev;
    b[i] = 2 * (1 / hPrev + 1 / hNext);
    c[i] = 1 / hNext;
  }
  for (const axis of ['x', 'y', 'z'] as const) {
    const rhs = new Float64Array(m);
    for (let i = 0; i < m; i++) {
      const hPrev = h[i];
      const hNext = h[i + 1];
      rhs[i] = 3 * ((P[i + 1][axis] - P[i][axis]) / (hPrev * hPrev) + (P[i + 2][axis] - P[i + 1][axis]) / (hNext * hNext));
      if (i === 0) rhs[i] -= a[i] * d0[axis];
      if (i === m - 1) rhs[i] -= c[i] * dn[axis];
    }
    const x = thomas(a, b, c, rhs);
    for (let i = 0; i < m; i++) {
      if (!D[i + 1]) D[i + 1] = new Vector3();
      D[i + 1][axis] = x[i];
    }
  }
  return D;
}

/** Periodic non-uniform spline for the fully closed track (cyclic tridiagonal). */
function smoothClosedAll(track: TrackDef): void {
  const n = track.nodes.length;
  const P = track.nodes.map((nd) => nd.p);
  const h: number[] = [];
  for (let i = 0; i < n; i++) h.push(Math.max(P[i].distanceTo(P[(i + 1) % n]), 1e-6));
  const D: Vector3[] = Array.from({ length: n }, () => new Vector3());

  const a = new Float64Array(n);
  const b = new Float64Array(n);
  const c = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const hPrev = h[(i - 1 + n) % n];
    const hNext = h[i];
    a[i] = 1 / hPrev;
    b[i] = 2 * (1 / hPrev + 1 / hNext);
    c[i] = 1 / hNext;
  }
  for (const axis of ['x', 'y', 'z'] as const) {
    const rhs = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      const hPrev = h[(i - 1 + n) % n];
      const hNext = h[i];
      const prev = P[(i - 1 + n) % n][axis];
      const next = P[(i + 1) % n][axis];
      rhs[i] = 3 * ((P[i][axis] - prev) / (hPrev * hPrev) + (next - P[i][axis]) / (hNext * hNext));
    }
    const x = cyclicThomas(a, b, c, rhs);
    for (let i = 0; i < n; i++) D[i][axis] = x[i];
  }
  for (let i = 0; i < n; i++) {
    const node = track.nodes[i];
    node.hOut.copy(D[i]).multiplyScalar(h[i] / 3);
    node.hIn.copy(D[i]).multiplyScalar(-h[(i - 1 + n) % n] / 3);
    node.mode = 'smooth';
  }
}

/** Standard Thomas algorithm for a tridiagonal system. */
function thomas(a: Float64Array, b: Float64Array, c: Float64Array, d: Float64Array): Float64Array {
  const n = d.length;
  const cp = new Float64Array(n);
  const dp = new Float64Array(n);
  cp[0] = c[0] / b[0];
  dp[0] = d[0] / b[0];
  for (let i = 1; i < n; i++) {
    const m = b[i] - a[i] * cp[i - 1];
    cp[i] = c[i] / m;
    dp[i] = (d[i] - a[i] * dp[i - 1]) / m;
  }
  const x = new Float64Array(n);
  x[n - 1] = dp[n - 1];
  for (let i = n - 2; i >= 0; i--) x[i] = dp[i] - cp[i] * x[i + 1];
  return x;
}

/**
 * Cyclic tridiagonal via Sherman–Morrison. Corner entries: A[0][n−1] = a[0]
 * (coupling to the previous-around-the-loop node) and A[n−1][0] = c[n−1].
 */
function cyclicThomas(a: Float64Array, b: Float64Array, c: Float64Array, d: Float64Array): Float64Array {
  const n = d.length;
  if (n === 1) return Float64Array.from([d[0] / (a[0] + b[0] + c[0])]);
  if (n === 2) {
    const b0 = b[0];
    const b1 = b[1];
    const s01 = c[0] + a[0]; // both couplings 0↔1 (direct + wrap)
    const s10 = a[1] + c[1];
    const det = b0 * b1 - s01 * s10;
    return Float64Array.from([(b1 * d[0] - s01 * d[1]) / det, (b0 * d[1] - s10 * d[0]) / det]);
  }
  const alpha = c[n - 1]; // A[n−1][0]
  const beta = a[0]; // A[0][n−1]
  const gamma = -b[0];
  const bb = Float64Array.from(b);
  bb[0] = b[0] - gamma;
  bb[n - 1] = b[n - 1] - (alpha * beta) / gamma;
  const x1 = thomas(a, bb, c, d);
  const u = new Float64Array(n);
  u[0] = gamma;
  u[n - 1] = alpha;
  const x2 = thomas(a, bb, c, u);
  const fact = (x1[0] + (beta / gamma) * x1[n - 1]) / (1 + x2[0] + (beta / gamma) * x2[n - 1]);
  const x = new Float64Array(n);
  for (let i = 0; i < n; i++) x[i] = x1[i] - fact * x2[i];
  return x;
}
